import RestoreIcon from '@mui/icons-material/Restore';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import type { PopoverOrigin } from '@mui/material';
import {
    Button,
    debounce,
    OutlinedInput,
    Popover,
    Stack,
    styled,
    Typography,
    useMediaQuery,
} from '@mui/material';
import type { MouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import type { Dispatch } from 'redux';

import { mediumScreen } from '@/src/shared/constants/MediaQueryConstants';
import { useBoolean, useInputValue } from '@/src/shared/hooks';
import {
    deleteConversations,
    getConversationHistory,
    getConversationSummaries,
} from '@/src/shared/services/apiServices/chatApis';
import { chatActions, selectConversationSummaryState } from '@/src/store/apps/chat';
import { borderColor, greyColor, inputBackground } from '@/src/styles/theme/lightThemeOptions';
import { throttle } from '@/src/utils/commonUtil';

import CancelDialog from '../../Modal/Cancel/CancelDialog';
import { ConversationSummaryList } from './ConversationSummaryList';

const DebounceTimeout = 1000;
const DefaultAnchorOrigin: PopoverOrigin = {
    vertical: 'center',
    horizontal: 'right',
};
const DefaultTransformOrigin: PopoverOrigin = {
    vertical: 'center',
    horizontal: 'left',
};

interface RecentPopoverProps {
    open: boolean;
    anchorEl?: Element;
    onDrawerClose: () => void;
    onClose: () => void;
}

interface SearchCondition {
    searchText: string;
    beforeTimestamp: string;
    isLastPage: boolean;
}

const DefaultSearchCondition: SearchCondition = {
    searchText: '',
    beforeTimestamp: '',
    isLastPage: false,
};

const DeleteConfirmationDialog = ({
    open,
    onConfirm,
    onClose,
}: {
    open: boolean;
    onConfirm: () => void;
    onClose: () => void;
}) => {
    const { t } = useTranslation('modal');
    return (
        <CancelDialog
            open={open}
            handleClose={onClose}
            title={t('modal.cancel.title')}
            dialogContent={t('modal.confirmation.delete.conversation_history')}
            buttons={[
                {
                    label: t('modal.delete.button.NO'),
                    variant: 'outlined',
                    handleClick: onClose,
                },
                {
                    label: t('modal.delete.button.OK'),
                    variant: 'contained',
                    handleClick: onConfirm,
                },
            ]}
            isErrorIconShow={true}
        />
    );
};

const useLoadHistoryEffect = (loadingHistoryForConversationId: string, dispatch: Dispatch) => {
    useEffect(() => {
        if (!loadingHistoryForConversationId) {
            return;
        }
        const task = async () => {
            const messages = await getConversationHistory({
                conversationId: loadingHistoryForConversationId,
            });
            dispatch(
                chatActions.appendLoadedConversationHistory({
                    conversationId: loadingHistoryForConversationId,
                    messages,
                })
            );
        };
        task();
    }, [loadingHistoryForConversationId]);
};

export const useRecentPopover = () => {
    const [recentPopoverAnchorEl, setRecentPopoverAnchorEl] = useState<HTMLElement | undefined>(
        undefined
    );
    const openRecentPopover = useCallback((event: MouseEvent<HTMLElement>) => {
        setRecentPopoverAnchorEl(event.currentTarget);
    }, []);
    const closeRecentPopover = useCallback(() => {
        setRecentPopoverAnchorEl(undefined);
    }, []);

    return { recentPopoverAnchorEl, openRecentPopover, closeRecentPopover };
};

const WrapperStack = styled(Stack)({
    width: '340px',
    height: '512px',
    padding: '16px',
    gap: '8px',
    fontFamily: 'Equip_Regular',
    '& .recent-text': {
        fontWeight: '700',
        fontSize: '14px',
        lineHeight: '20px',
    },
    '& .header .MuiButton-root': {
        height: '32px',
        padding: '6px 12px',
        textTransform: 'none',
    },
    '& .MuiOutlinedInput-root': {
        height: '32px',
        backgroundColor: inputBackground,
        '& fieldset': {
            border: `2px solid ${borderColor}`,
        },
        '& .MuiSvgIcon-root': {
            color: greyColor,
            marginRight: '8px',
        },
    },
    '& .conversation-list': {
        flex: 1,
        overflowY: 'auto',
    },
});

export const RecentPopover = ({ open, anchorEl, onDrawerClose, onClose }: RecentPopoverProps) => {
    const { t } = useTranslation('common');
    const isMobile: boolean = useMediaQuery(mediumScreen);
    const searchConditionRef = useRef<SearchCondition>(DefaultSearchCondition);
    const [searchInputValue, onSearchInputChange] = useInputValue('');
    const dispatch = useDispatch();
    const [selectedSummaryIds, setSelectedSummaryIds] = useState<string[]>([]);
    const [isLoading, { setTrue: startLoading, setFalse: stopLoading }] = useBoolean(false);
    const { currentConversationId, loadingHistoryForConversationId, conversationSummaries } =
        useSelector(selectConversationSummaryState);

    const [isSelectionMode, { setTrue: enterSelectionMode, setFalse: exitSelectionMode }] =
        useBoolean(false);
    const [
        isDeleteConfirmationOpen,
        { setTrue: openDeleteConfirmation, setFalse: closeDeleteConfirmation },
    ] = useBoolean(false);

    const switchCurrentConversation = useCallback(
        (conversationId: string) => {
            if (conversationId === currentConversationId) {
                return;
            }
            dispatch(chatActions.setLoadingHistoryForConversationId(conversationId));
            if (isMobile) onDrawerClose();
            onClose();
        },
        [currentConversationId, onClose]
    );

    const deleteConversationsBySummaryIds = useCallback(async () => {
        closeDeleteConfirmation();
        await deleteConversations(selectedSummaryIds);
        if (selectedSummaryIds.includes(currentConversationId)) {
            dispatch(chatActions.switchToNewConversation());
        }
        dispatch(chatActions.removeConversations(selectedSummaryIds));
        exitSelectionMode();
        setSelectedSummaryIds([]);
    }, [selectedSummaryIds, currentConversationId]);
    const onDeleteOne = useCallback(
        (conversationId: string) => {
            setSelectedSummaryIds([conversationId]);
            openDeleteConfirmation();
        },
        [deleteConversationsBySummaryIds]
    );

    const searchConversationSummaries = useCallback(async () => {
        if (searchConditionRef.current.isLastPage) {
            return;
        }

        startLoading();
        try {
            const { beforeTimestamp, searchText } = searchConditionRef.current;
            const { conversationSummaries: newConversationSummaries, isLastPage } =
                await getConversationSummaries({ beforeTimestamp, searchText });

            searchConditionRef.current.isLastPage = isLastPage;
            if (beforeTimestamp) {
                dispatch(chatActions.appendConversationSummaries(newConversationSummaries));
                return;
            }
            dispatch(chatActions.setConversationSummaries(newConversationSummaries));
        } finally {
            stopLoading();
        }
    }, [searchConditionRef]);
    const throttledSearch = useMemo(() => {
        return throttle(searchConversationSummaries, DebounceTimeout);
    }, [searchConversationSummaries]);
    useEffect(() => {
        searchConditionRef.current = {
            searchText: searchInputValue,
            beforeTimestamp: '',
            isLastPage: false,
        };
        startLoading();
        const debouncedFn = debounce(searchConversationSummaries, DebounceTimeout);
        debouncedFn();
        return () => {
            debouncedFn.clear();
        };
    }, [searchInputValue, searchConversationSummaries]);
    useEffect(() => {
        const hasLoaded = !!searchConditionRef.current.beforeTimestamp;
        searchConditionRef.current.beforeTimestamp = conversationSummaries.length
            ? conversationSummaries[conversationSummaries.length - 1].lastQueryTimestamp
            : '';
        // auto refresh after deleted all
        if (hasLoaded && !conversationSummaries.length && !searchConditionRef.current.isLastPage) {
            searchConversationSummaries();
        }
    }, [conversationSummaries, searchConversationSummaries]);
    useEffect(() => {
        setSelectedSummaryIds([]);
    }, [isSelectionMode]);
    useLoadHistoryEffect(loadingHistoryForConversationId, dispatch);

    return (
        <Popover
            anchorOrigin={DefaultAnchorOrigin}
            transformOrigin={DefaultTransformOrigin}
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
        >
            <WrapperStack>
                <Stack
                    className="header"
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Stack direction="row" gap="6px" alignItems="center">
                        <RestoreIcon />
                        <Typography variant="body1" className="recent-text">
                            {t('web_channel.conversation.recent')}
                        </Typography>
                    </Stack>
                    {isSelectionMode ? (
                        <Stack direction="row" gap="6px">
                            <Button
                                variant="contained"
                                disabled={selectedSummaryIds.length === 0}
                                onClick={openDeleteConfirmation}
                            >
                                {t('action.delete')}
                            </Button>
                            <Button variant="outlined" onClick={exitSelectionMode}>
                                {t('modal.button.error.cancel')}
                            </Button>
                        </Stack>
                    ) : (
                        <Button
                            variant="outlined"
                            disabled={isLoading || !conversationSummaries.length}
                            onClick={enterSelectionMode}
                        >
                            {t('select')}
                        </Button>
                    )}
                </Stack>
                <OutlinedInput
                    size="small"
                    placeholder={t('search') || ''}
                    value={searchInputValue}
                    onChange={onSearchInputChange}
                    startAdornment={<SearchOutlinedIcon fontSize="small" />}
                />
                <ConversationSummaryList
                    isSelectionMode={isSelectionMode}
                    isLoading={isLoading}
                    isLastPage={searchConditionRef.current.isLastPage}
                    selectedSummaryIds={selectedSummaryIds}
                    setSelectedSummaryIds={setSelectedSummaryIds}
                    currentConversationId={currentConversationId}
                    conversationSummaries={conversationSummaries}
                    switchCurrentConversation={switchCurrentConversation}
                    onLoadMore={throttledSearch}
                    onDelete={onDeleteOne}
                />
            </WrapperStack>
            <DeleteConfirmationDialog
                open={isDeleteConfirmationOpen}
                onConfirm={deleteConversationsBySummaryIds}
                onClose={closeDeleteConfirmation}
            />
        </Popover>
    );
};
